import React, { useCallback, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import { firstExhibitorAtBooth } from '../../tools/boothExhibitors';
import translation from '../../signals/translations';
import IconClose from '../../icons/IconClose';

const RouteSharePopup = () => {
	const { RouteSharePopup: routeData } = appState.value;
	const { basicInfo, exhibitorInfo } = jsonData.value;

	const [copiedLink, setCopiedLink] = useState(false);
	const [copiedSummary, setCopiedSummary] = useState(false);

	const isOpen = routeData !== null;

	const routeUrl = useMemo(() => {
		if (!routeData) return '';
		const base = `${window.location.origin}${window.location.pathname}`;
		return `${base}?route=${routeData.id}`;
	}, [routeData]);

	const boothStops = useMemo(() => {
		if (!routeData) return [];
		return routeData.stops
			.filter((s) => s['點位類型'] === '攤位')
			.sort((a, b) => Number(a['站序']) - Number(b['站序']))
			.map((stop, index) => {
				const boothNumber = stop['攤位號碼'];
				const exhibitor = firstExhibitorAtBooth(exhibitorInfo, boothNumber);
				const intro = stop['廠商介紹'] || (exhibitor && exhibitor['展商基本介紹']) || '';
				return {
					index: index + 1,
					boothNumber,
					name: exhibitor ? exhibitor['廠商簡稱'] || exhibitor['廠商名稱'] : boothNumber,
					intro,
				};
			});
	}, [routeData, exhibitorInfo]);

	const close = useCallback(
		(event) => {
			if (event.target === event.currentTarget) {
				appState.value = {
					...appState.value,
					RouteSharePopup: null,
				};
			}
		},
		[]
	);

	const closePopup = () => {
		appState.value = {
			...appState.value,
			RouteSharePopup: null,
		};
	};

	const copyLink = () => {
		navigator.clipboard.writeText(routeUrl);
		setCopiedLink(true);
		setTimeout(() => setCopiedLink(false), 1500);
	};

	const copySummary = () => {
		if (!routeData) return;
		const lines = [];
		lines.push(`${translation('route.exhibitionName')}：${basicInfo.name}`);
		lines.push(`${translation('route.routeName')}：${routeData.name}`);
		if (routeData.description) {
			lines.push(`${translation('route.routeIntro')}：${routeData.description}`);
		}
		lines.push('');
		boothStops.forEach((stop) => {
			lines.push(`第${stop.index}站：${stop.boothNumber} ${stop.name}`);
			if (stop.intro) lines.push(stop.intro);
		});
		navigator.clipboard.writeText(lines.join('\n'));
		setCopiedSummary(true);
		setTimeout(() => setCopiedSummary(false), 1500);
	};

	if (!isOpen || !routeData) return null;

	return (
		<div id='RouteSharePopup' className={isOpen ? 'active' : ''} onClick={close}>
			<div className='center-content'>
				<IconClose onClick={closePopup} />

				<div className='qr-section'>
					<div className='qr-title'>{translation('route.qrCode')}</div>
					<div className='qr-code'>
						<QRCodeSVG value={routeUrl} size={200} />
					</div>
				</div>

				<div className='actions'>
					<div className={`action-button ${copiedSummary ? 'copied' : ''}`} onClick={copySummary}>
						<span className='material-symbols-rounded'>{copiedSummary ? 'check' : 'content_copy'}</span>
						{copiedSummary ? '已複製' : translation('route.copySummary')}
					</div>
					<div className={`action-button ${copiedLink ? 'copied' : ''}`} onClick={copyLink}>
						<span className='material-symbols-rounded'>{copiedLink ? 'check' : 'link'}</span>
						{copiedLink ? '已複製' : translation('route.copyLink')}
					</div>
				</div>

				<div className='summary-section'>
					<div className='summary-title'>{translation('route.routeSummary')}</div>
					<div className='summary-row'>
						<span className='summary-label'>{translation('route.exhibitionName')}：</span>
						{basicInfo.name}
					</div>
					<div className='summary-row'>
						<span className='summary-label'>{translation('route.routeName')}：</span>
						{routeData.name}
					</div>
					{routeData.description ? (
						<div className='summary-row'>
							<span className='summary-label'>{translation('route.routeIntro')}：</span>
							{routeData.description}
						</div>
					) : null}
					{boothStops.map((stop) => (
						<React.Fragment key={stop.index}>
							<div className={`summary-row${stop.intro ? ' has-intro' : ''}`}>
								第{stop.index}站：{stop.boothNumber} {stop.name}
							</div>
							{stop.intro ? <div className='summary-row stop-intro'>{stop.intro}</div> : null}
						</React.Fragment>
					))}
				</div>
			</div>
		</div>
	);
};

export default RouteSharePopup;
